import {t} from '../../i18n/i18n.js';
import {editedPagePath, pageUrlPath} from '../../core/page/page.core.js';
import {PageKey} from '../../core/vocabulary.js';

/**
 * Live preview of the URL the slug resolves to, shown under the slug field. It follows the
 * slug and, while the slug is empty, the title (see {@link bindSlugPreview}).
 * @returns {HTMLDivElement}
 */
export function createSlugPreviewPill() {
    const pill = document.createElement('div');
    pill.className = 'slug-preview-pill';
    pill.setAttribute('aria-live', 'polite');
    pill.setAttribute('aria-label', t('pageForm.slugPreviewLabel'));
    return pill;
}

/**
 * Warning under the preview pill, holding text while the predicted address of a live page
 * differs from its current one (see {@link renderSlugPreview}). It stays in the accessibility
 * tree as an empty status region, so screen readers announce the text when it appears; the
 * stylesheet collapses it while empty.
 * @returns {HTMLParagraphElement}
 */
export function createSlugMoveWarning() {
    const warning = document.createElement('p');
    warning.className = 'slug-move-warning';
    warning.setAttribute('role', 'status');
    return warning;
}

/**
 * Refreshes the preview pill on every slug or title keystroke. Call once the whole field
 * tree is built; no-ops without a pill.
 * @param {ParentNode} root
 * @param {() => void} update
 */
export function bindSlugPreview(root, update) {
    if (!root.querySelector('.slug-preview-pill')) return;
    root.querySelector(`[name="${PageKey.SLUG}"]`)?.addEventListener('input', update);
    root.querySelector(`[name="${PageKey.TITLE}"]`)?.addEventListener('input', update);
    update();
}

/**
 * Renders the pill from the current slug and title values, in the current language. Safe to
 * call before the pill exists (no-ops). The path comes from `editedPagePath`, an exact
 * mirror of the backend's path derivation (slug prefix, title and template-name fallbacks,
 * folders, fragments/, and a live page keeping an address its slug source still gives under
 * the slug rules from before accents were transliterated). It also fills the address-change warning while the predicted address
 * differs from `livePath`, the file path of the live page (null when the page has no live
 * address), and empties it otherwise. Both sides are compared as public URL paths, so the
 * `.html` extension and the index page do not count as a change.
 *
 * @param {ParentNode} root
 * @param {{templateName: string, slugPrefix: string | null, domain: string | null, mode: import('../../core/vocabulary.js').RenderMode, homepage?: boolean, livePath?: string | null}} page
 */
export function renderSlugPreview(root, {templateName, slugPrefix, domain, mode, homepage, livePath}) {
    const pill = root.querySelector('.slug-preview-pill');
    if (!pill) return;

    const slugInput = root.querySelector(`[name="${PageKey.SLUG}"]`);
    const titleInput = root.querySelector(`[name="${PageKey.TITLE}"]`);
    const preview = editedPagePath({
        slug: slugInput?.value || '',
        title: titleInput?.value || '',
        templateName,
        slugPrefix,
        domain,
        mode,
        homepage,
    }, livePath);

    const moved = Boolean(livePath) && pageUrlPath(preview.path) !== pageUrlPath(livePath);
    // Rewriting an unchanged text would make screen readers announce the status region again on every key.
    const warning = root.querySelector('.slug-move-warning');
    const warningText = moved ? t('pageForm.slugMoveWarning') : '';
    if (warning.textContent !== warningText) warning.textContent = warningText;
    pill.setAttribute('aria-label', t('pageForm.slugPreviewLabel'));

    // Strip protocol for display; users recognize the bare host more readily.
    const host = preview.domain ? preview.domain.replace(/^https?:\/\//, '') : '';

    pill.replaceChildren();

    const icon = document.createElement('i');
    icon.className = 'fa-solid fa-link slug-preview-icon';
    icon.setAttribute('aria-hidden', 'true');
    pill.appendChild(icon);

    const url = document.createElement('span');
    url.className = 'slug-preview-url';
    if (host) {
        const hostSpan = document.createElement('span');
        hostSpan.className = 'slug-preview-host';
        hostSpan.textContent = host;
        url.appendChild(hostSpan);
        url.appendChild(document.createTextNode('/'));
    }
    if (preview.prefix) {
        const prefixSpan = document.createElement('span');
        prefixSpan.className = 'slug-preview-prefix';
        prefixSpan.textContent = preview.prefix;
        url.appendChild(prefixSpan);
    }
    const slugSpan = document.createElement('span');
    slugSpan.className = 'slug-preview-slug';
    slugSpan.textContent = preview.slug;
    url.appendChild(slugSpan);
    pill.appendChild(url);

    // Re-trigger the pulse animation by toggling the class across a frame.
    pill.classList.remove('is-pulse');
    // Force reflow so the next class add restarts the animation reliably.
    void pill.offsetWidth;
    pill.classList.add('is-pulse');
}
